import { useMemo, useState, useEffect } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useTimeZones, useUpdateTimeZoneActive, errMessage, type TimeZoneRow } from './systemMasters';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Time Zones — a fixed reference list (no New / Delete, as legacy
 * TimeZones_ListView). Each row has an inline-edit pencil, as legacy: Time Zone and UTC stay
 * read-only and only the Active tick can be changed, then Save / Cancel in the row.
 */
export function TimeZonesPage({ onHome }: Props) {
  const { data: rows = [], isFetching, refetch } = useTimeZones();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const notify = useNotify();
  const gateReason = useReasonGate();
  const update = useUpdateTimeZoneActive();
  // Inline row edit: the row being edited and its Active tick.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [draftActive, setDraftActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const startInline = (r: TimeZoneRow) => { setInlineId(r.id); setDraftActive(r.active); };
  const cancelInline = () => setInlineId(null);
  const saveInline = async () => {
    if (!inlineId) return;
    // Reason-for-change gate (standing rule 11); null = the user cancelled the prompt.
    const reason = await gateReason('Time Zones', 'edit');
    if (reason === null) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => update.mutateAsync({ id: inlineId, active: draftActive }));
      notify.success('Saved successfully.');
      setInlineId(null);
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSaving(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.timeZone, r.utc].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const columns: AppColumn<TimeZoneRow>[] = [
    {
      field: 'inlineedit', header: '', noExport: true, sortable: false, filterable: false, width: 56, align: 'center', text: () => '',
      render: (r) =>
        inlineId === r.id ? (
          <span style={{ display: 'inline-flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={saving} onClick={() => void saveInline()}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={saving} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" aria-label={`Edit ${r.timeZone ?? 'time zone'}`} onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-pencil" /></button>
        ),
    },
    { field: 'timeZone', header: 'TimeZone', minWidth: 320, text: (r) => r.timeZone ?? '',
      compare: (a, b) => (a.timeZone ?? '').localeCompare(b.timeZone ?? '', undefined, { sensitivity: 'base' }) },
    { field: 'utc', header: 'UTC', width: 140, sortable: false, text: (r) => r.utc ?? '' },
    { field: 'active', header: 'Active', width: 110, align: 'center', sortable: false, text: (r) => (r.active ? 'Yes' : 'No'),
      render: (r) => inlineId === r.id
        ? <input type="checkbox" aria-label="Active" checked={draftActive} disabled={saving} onClick={(e) => e.stopPropagation()} onChange={(e) => setDraftActive(e.target.checked)} />
        : <span className={`cellcheck${r.active ? ' on' : ''}`} role="img" aria-label={r.active ? 'Enabled' : 'Disabled'} /> },
  ];

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Time Zones</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-timezones" auditFormName="Time Zones" loadMs={loadMs ?? null} loading={isFetching} />
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        {!gridMax && (
        <AppToolbar
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
            <ExportMenu triggerClassName="ca-btn" filename="TimeZones" headers={['TimeZone', 'UTC', 'Active']} rows={visible.map((r) => [r.timeZone ?? '', r.utc ?? '', r.active ? 'Yes' : 'No'])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="TimeZones" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<TimeZoneRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectedRowId={inlineId}
            initialSort={{ key: 'timeZone', dir: 'asc' }} loading={isFetching} emptyText="No time zones found." hideHeaderTools />
        </div>
      </div>
    </div>
  );
}
